'use client'

import React, { useState } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui'
import { Card, CardContent } from '@/components/ui'
import { Check } from 'lucide-react'
import { createClientComponentClient } from '@supabase/auth-helpers-nextjs'

interface PlanPreview {
  id: string
  title: string
  price: string
  singlePayment: number
  highlights: string[]
  badge?: string
}

interface PlanSelectionModalProps {
  isOpen: boolean
  onClose: () => void
  plans?: PlanPreview[]
}

const DEFAULT_PLANS: PlanPreview[] = [
  {
    id: 'basic',
    title: '基本方案',
    price: 'NT$ 390 / 月',
    singlePayment: 390,
    highlights: [
      '解鎖：N5~N1 完整學習內容',
      '解鎖新角：高木 大和'
    ]
  },
  {
    id: 'vip-month',
    title: 'VIP 升級全角色語音',
    price: 'NT$ 550 / 月',
    singlePayment: 550,
    highlights: [
      '解鎖：N5~N1 完整學習內容',
      '解鎖：全角色語音服務'
    ],
    badge: '熱門'
  },
  {
    id: 'vip-year',
    title: 'VIP 7折 年繳超值方案',
    price: 'NT$ 4,620 / 年（等於一個月只要 NT$ 385）',
    singlePayment: 3980,
    highlights: [
      '解鎖：N5~N1 完整學習內容',
      '解鎖：全角色語音服務一整年！'
    ],
    badge: '最划算'
  }
].sort((a, b) => a.singlePayment - b.singlePayment)

export const PlanSelectionModal: React.FC<PlanSelectionModalProps> = ({
  isOpen,
  onClose,
  plans = DEFAULT_PLANS
}) => {
  const router = useRouter()
  const supabase = createClientComponentClient()
  const [planModalLoading, setPlanModalLoading] = useState(false)

  const handlePlanConfirm = async () => {
    try {
      setPlanModalLoading(true)
      const { data: { user } } = await supabase.auth.getUser()
      onClose()
      if (user) {
        router.push('/subscribe')
      } else {
        router.push('/auth/login?redirect=/subscribe')
      }
    } finally {
      setPlanModalLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 text-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-purple-500">
        <div className="p-6 border-b border-white/10 text-center space-y-2">
          <h2 className="text-2xl font-bold">選擇適合你的方案</h2>
          <p className="text-sm text-gray-300">
            了解方案內容後請登入並完成訂閱
          </p>
        </div>
        <div className="p-6 space-y-6">
          {plans.map(plan => (
            <Card 
              key={plan.id} 
              onClick={handlePlanConfirm}
              className={`border border-purple-300 bg-gradient-to-br from-purple-900/40 to-blue-900/30 relative cursor-pointer transition-all hover:scale-[1.02] hover:border-purple-400 hover:shadow-lg ${
                planModalLoading ? 'opacity-60 cursor-not-allowed pointer-events-none' : ''
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-black px-4 py-0.5 rounded-full text-xs font-semibold shadow z-10">
                  {plan.badge}
                </div>
              )}
              <CardContent className="p-5 space-y-3">
                <div className="text-center">
                  <h3 className="text-lg font-semibold">{plan.title}</h3>
                  {plan.id === 'vip-year' ? (
                    <p className="text-sm text-gray-200 mt-1">
                      NT$ 3,980 / 年<br />
                      （等於一個月只要 NT$ 332）
                    </p>
                  ) : (
                    <p className="text-sm text-gray-200 mt-1">{plan.price}</p>
                  )}
                </div>
                <div className="space-y-2 text-sm text-gray-100 text-center">
                  {plan.highlights.map(line => (
                    <div key={line} className="flex items-start justify-center">
                      <Check className="h-4 w-4 text-green-400 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="break-words text-center">{line}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="p-6 border-t border-white/10 flex flex-col sm:flex-row gap-3">
          <Button
            onClick={handlePlanConfirm}
            disabled={planModalLoading}
            className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
          >
            {planModalLoading ? '處理中…' : '我了解方案，前往登入'}
          </Button>
          <Button
            onClick={onClose}
            variant="secondary"
            className="flex-1"
          >
            暫不升級
          </Button>
        </div>
      </div>
    </div>
  )
}

